// Sources & Ingest — the provenance control plane

import { pbAuthedFetch } from '../authHeaders';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

export function SourcesPage() {
  const queryClient = useQueryClient();
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [uploadData, setUploadData] = useState<string>('');
  const [uploadSource, setUploadSource] = useState<string>('');

  const { data, isLoading, error: sourcesError, refetch } = useQuery({
    queryKey: ['sources'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/sources');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: runsData } = useQuery({
    queryKey: ['ingest-runs', selectedSource],
    queryFn: async () => {
      if (!selectedSource) return { runs: [] };
      const res = await pbAuthedFetch(`/api/sources/${selectedSource}/runs`);
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    enabled: !!selectedSource,
  });

  const ingestMutation = useMutation({
    mutationFn: async ({ source_key, data }: { source_key: string; data: any[] }) => {
      const res = await pbAuthedFetch('/api/ingest/file', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source_key, data }),
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources'] });
      queryClient.invalidateQueries({ queryKey: ['reconciliation-summary'] });
      setUploadData('');
      setUploadSource('');
    },
  });

  const sources = data?.sources || [];
  const runs = runsData?.runs || [];

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setUploadData(content);
    };
    reader.readAsText(file);
  };

  const handleIngest = () => {
    if (!uploadSource || !uploadData) return;

    // Parse CSV to JSON
    const lines = uploadData.split('\n').filter(l => l.trim());
    const headers = lines[0].split(',').map(h => h.trim());
    const records = lines.slice(1).map(line => {
      const values = line.split(',');
      const record: any = {};
      headers.forEach((h, i) => {
        record[h] = values[i]?.trim();
      });
      return record;
    });

    ingestMutation.mutate({ source_key: uploadSource, data: records });
  };

  const selectedSourceData = sources.find((s: any) => s.id === selectedSource);

  if (isLoading) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Provenance</p>
              <h1 className="pagehead__title">Sources &amp; Ingest</h1>
              <div className="pagehead__facts">
                <span className="fact">Provenance control plane</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="page-loading" role="status" aria-live="polite">
                Loading sources…
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (sourcesError) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Provenance</p>
              <h1 className="pagehead__title">Sources &amp; Ingest</h1>
              <div className="pagehead__facts">
                <span className="fact">Provenance control plane</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="state state--risk">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="state__title">Failed to load sources</h3>
                <p className="state__body">
                  Could not connect to the sources API. Please check your connection and try again.
                </p>
                <button className="btn btn--primary" onClick={() => refetch()}>
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">Provenance</p>
            <h1 className="pagehead__title">Sources &amp; Ingest</h1>
            <div className="pagehead__facts">
              <span className="fact">Provenance control plane — manage connectors and ingest data</span>
              <span className="fact">Sources: <b>{sources.length}</b></span>
            </div>
          </div>
        </div>
      </div>

      <div className="pagebody">
        {/* File ingest */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Ingest</p>
              <h2 className="box__title">File Ingest</h2>
            </div>
          </div>
          <div className="box__body">
            <div style={{ display: 'flex', gap: 'var(--sp-4)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="field">
                <label>Source</label>
                <select
                  value={uploadSource}
                  onChange={e => setUploadSource(e.target.value)}
                >
                  <option value="">Select source…</option>
                  {sources.map((s: any) => (
                    <option key={s.id} value={s.key}>{s.display_name}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>CSV File</label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                />
              </div>
              <button
                className="btn btn--primary"
                onClick={handleIngest}
                disabled={!uploadSource || !uploadData || ingestMutation.isPending}
              >
                {ingestMutation.isPending ? 'Ingesting…' : 'Ingest'}
              </button>
            </div>
            {ingestMutation.isSuccess && (
              <div className="notice notice--ok" role="status" aria-live="polite" style={{ marginTop: 'var(--sp-3)' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <div>
                  <p className="notice__title">Ingest complete</p>
                  <p className="notice__body">
                    {ingestMutation.data?.run?.records_new} new, {ingestMutation.data?.run?.records_matched} matched, {ingestMutation.data?.run?.records_conflicted} conflicted
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Source cards */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Connectors</p>
              <h2 className="box__title">Configured Sources</h2>
            </div>
          </div>
          <div className="box__body">
            {sources.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 'var(--sp-4)' }} role="group" aria-label="Configured sources">
                {sources.map((source: any) => (
                  <div
                    key={source.id}
                    className="box"
                    style={{
                      cursor: 'pointer',
                      borderColor: selectedSource === source.id ? 'var(--gold)' : undefined,
                    }}
                    onClick={() => setSelectedSource(source.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedSource(source.id); } }}
                    tabIndex={0}
                    role="button"
                    aria-pressed={selectedSource === source.id}
                    aria-label={`${source.display_name} source — ${source.kind}`}
                  >
                    <div className="box__head">
                      <div className="box__head-text">
                        <h3 className="box__title" style={{ fontSize: 'var(--text-lg)' }}>{source.display_name}</h3>
                      </div>
                      <div className="box__head-actions">
                        <span className="pill"><i></i>{source.kind}</span>
                      </div>
                    </div>
                    <div className="box__body">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
                        <span className="fact">Mode: <b>{source.mode}</b></span>
                        <span className="fact">Trust weight: <b>{source.trust_weight}</b>/100</span>
                        <span className="fact">
                          Last run: {source.last_successful_run_at
                            ? new Date(source.last_successful_run_at).toLocaleString()
                            : 'Never'}
                        </span>
                        <span className="fact">Total runs: <b>{source.total_runs || 0}</b></span>
                      </div>
                      {source.is_authoritative_for?.length > 0 && (
                        <div style={{ marginTop: 'var(--sp-2)', fontSize: 'var(--text-xs)', fontFamily: 'var(--mono)', color: 'var(--ink-45)', letterSpacing: '.04em', textTransform: 'uppercase' }}>
                          Authoritative for: {source.is_authoritative_for.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <h3 className="state__title">No sources configured</h3>
                <p className="state__body">
                  Sources will be created when you seed the database or ingest data.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Ingest run history */}
        {selectedSource && runs.length > 0 && (
          <div className="box">
            <div className="box__head">
              <div className="box__head-text">
                <p className="box__eyebrow">History</p>
                <h2 className="box__title">Ingest Run History{selectedSourceData ? ` — ${selectedSourceData.display_name}` : ''}</h2>
              </div>
            </div>
            <div className="box__body box__body--flush">
              <div className="tbl-scroll">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th><button className="th th--static">Started</button></th>
                      <th><button className="th th--static">Status</button></th>
                      <th><button className="th th--num">Records In</button></th>
                      <th><button className="th th--num">Matched</button></th>
                      <th><button className="th th--num">New</button></th>
                      <th><button className="th th--num">Conflicted</button></th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((run: any) => (
                      <tr key={run.id}>
                        <td style={{ fontSize: 'var(--text-sm)', fontFamily: 'var(--mono)', color: 'var(--ink-45)' }}>
                          {new Date(run.started_at).toLocaleString()}
                        </td>
                        <td>
                          <span className={`pill pill--${run.status === 'success' ? 'ok' : 'risk'}`}>
                            <i></i>
                            {run.status}
                          </span>
                        </td>
                        <td className="td--num">{run.records_in}</td>
                        <td className="td--num">{run.records_matched}</td>
                        <td className="td--num">{run.records_new}</td>
                        <td className="td--num">{run.records_conflicted}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {selectedSource && runs.length === 0 && (
          <div className="box">
            <div className="box__head">
              <div className="box__head-text">
                <p className="box__eyebrow">History</p>
                <h2 className="box__title">Ingest Run History</h2>
              </div>
            </div>
            <div className="box__body">
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="state__title">No runs yet</h3>
                <p className="state__body">
                  This source has no ingest run history. Upload a file above to trigger a run.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
